
import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import SignatureCanvas from 'react-signature-canvas';
import Swal from 'sweetalert2';
import { handleImageUpload } from '../../api/uploads';

const AllergyActionPlanFormComp = ({ onSubmit, onCancel, remarks, defaultData }) => {
    const signaturePadRef = useRef(null);
    const [formData, setFormData] = useState({
        is_peanuts: 0,
        is_shellfish: 0,
        shellfish_specify: '',
        is_tree_nuts: 0,
        is_fish: 0,
        fish_specify: '',
        is_milk: 0,
        is_food_additives: 0,
        food_additives_specify: '',
        all_dairy_products: 0,
        insect_stings_bites: 0,
        insect_stings_bites_specify: '',
        insect_stings_bites_medication: '',
        is_other: 0,
        other_specify: '',
        symptoms: '',
        emergency_treatment_procedures: '',
        require_epi_pen: 0,
        location_of_epi_pen: '',
        parent_signature: '',
        parent_signature_date: '',
        is_allergy_form: 0,
        is_preference_form: 0
    });

    useEffect(() => {
        if (defaultData) {
            setFormData(prev => ({
                ...prev,
                ...defaultData,
                is_allergy_form: defaultData.is_allergy_form ?? 0,
                is_preference_form: defaultData.is_preference_form ?? 0
            }));
        }
        // eslint-disable-next-line
    }, [defaultData]);

    const handleCheckboxChange = (field) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field] === 1 ? 0 : 1
        }));
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleClearSignature = () => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!formData.symptoms || !formData.emergency_treatment_procedures) {
            Swal.fire({
                icon: 'warning',
                title: 'Required Fields',
                text: 'Please fill in symptoms and emergency treatment procedures.'
            });
            return;
        }
        const hasNewSignature = signaturePadRef.current && !signaturePadRef.current.isEmpty();
        if (!hasNewSignature && !formData.parent_signature) {
            Swal.fire({
                icon: 'warning',
                title: 'Signature Required',
                text: 'Please provide parent signature.'
            });
            return;
        }
        if (!formData.parent_signature_date) {
            Swal.fire({
                icon: 'warning',
                title: 'Date Required',
                text: 'Please provide signature date.'
            });
            return;
        }
        let submitData = { ...formData };
        if (hasNewSignature) {
            const signatureDataURL = signaturePadRef.current.toDataURL('image/png');
            const signatureBlob = await fetch(signatureDataURL).then(res => res.blob());
            const signatureFile = new File([signatureBlob], `parent_signature_${Date.now()}.png`, { type: 'image/png' });
            const signatureUrl = await handleImageUpload(signatureFile, 'signatures/', 'image/png');
            submitData.parent_signature = signatureUrl;
        }
        
        // Show loader during submission
        Swal.fire({
            title: 'Processing...',
            html: 'Saving allergy action plan...',
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });
        
        try {
            await onSubmit(submitData);
            Swal.close();
        } catch (error) {
            Swal.close();
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.message || 'Failed to save allergy action plan. Please try again.'
            });
        }
    };

    return (
        <Container>
            <Row>
                <Col sm="12">
                    <Card>
                        {remarks &&
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <div className="header-title">
                                    <h4 className="card-title">
                                        {remarks}
                                    </h4>
                                </div>
                            </Card.Header>
                        }
                        <Card.Body>
                            <Form onSubmit={handleFormSubmit}>
                                <div className="mb-4">
                                    <Form.Check
                                        type="checkbox"
                                        id="is_allergy_form"
                                        label="Allergies"
                                        checked={!!formData.is_allergy_form}
                                        onChange={() => handleInputChange('is_allergy_form', formData.is_allergy_form ? 0 : 1)}
                                        className="mb-2 custom-checkbox"
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="is_preference_form"
                                        label="Preferences"
                                        style={{ marginLeft: '20px' }}
                                        checked={!!formData.is_preference_form}
                                        onChange={() => handleInputChange('is_preference_form', formData.is_preference_form ? 0 : 1)}
                                        className="mb-2 custom-checkbox"
                                    />
                                </div>
                                <h5 className="mb-3">Food Allergies</h5>
                                <Row className="mb-3">
                                    <Col md="6">
                                        <Form.Check
                                            type="checkbox"
                                            id="is_peanuts"
                                            label="Peanuts"
                                            checked={formData.is_peanuts === 1}
                                            onChange={() => handleCheckboxChange('is_peanuts')}
                                            className="mb-3 custom-checkbox"
                                        />
                                        <Form.Check
                                            type="checkbox"
                                            id="is_shellfish"
                                            label="Shellfish"
                                            checked={formData.is_shellfish === 1}
                                            onChange={() => handleCheckboxChange('is_shellfish')}
                                            className="mb-3 custom-checkbox"
                                            style={{ marginLeft: '20px' }}
                                        />
                                        {formData.is_shellfish === 1 && (
                                            <Form.Group className="mb-2 ms-4">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Specify shellfish type"
                                                    value={formData.shellfish_specify}
                                                    onChange={(e) => handleInputChange('shellfish_specify', e.target.value)}
                                                />
                                            </Form.Group>
                                        )}
                                        <Form.Check
                                            type="checkbox"
                                            id="is_tree_nuts"
                                            label="Tree Nuts"
                                            checked={formData.is_tree_nuts === 1}
                                            onChange={() => handleCheckboxChange('is_tree_nuts')}
                                            className="mb-3 custom-checkbox"
                                            style={{ marginLeft: '20px' }}
                                        />
                                        <Form.Check
                                            type="checkbox"
                                            id="is_fish"
                                            label="Fish"
                                            checked={formData.is_fish === 1}
                                            onChange={() => handleCheckboxChange('is_fish')}
                                            className="mb-3 custom-checkbox"
                                            style={{ marginLeft: '20px' }}
                                        />
                                        {formData.is_fish === 1 && (
                                            <Form.Group className="mb-2 ms-4">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Specify fish type"
                                                    value={formData.fish_specify}
                                                    onChange={(e) => handleInputChange('fish_specify', e.target.value)}
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                    <Col md="6">
                                        <Form.Check
                                            type="checkbox"
                                            id="is_milk"
                                            label="Milk"
                                            checked={formData.is_milk === 1}
                                            onChange={() => handleCheckboxChange('is_milk')}
                                            className="mb-3 custom-checkbox"
                                            style={{ marginLeft: '20px' }}
                                        />
                                        <Form.Check
                                            type="checkbox"
                                            id="is_food_additives"
                                            label="Food Additives"
                                            checked={formData.is_food_additives === 1}
                                            onChange={() => handleCheckboxChange('is_food_additives')}
                                            className="mb-3 custom-checkbox"
                                            style={{ marginLeft: '20px' }}
                                        />
                                        {formData.is_food_additives === 1 && (
                                            <Form.Group className="mb-2 ms-4">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Specify food additives"
                                                    value={formData.food_additives_specify}
                                                    onChange={(e) => handleInputChange('food_additives_specify', e.target.value)}
                                                />
                                            </Form.Group>
                                        )}
                                        <Form.Check
                                            type="checkbox"
                                            id="all_dairy_products"
                                            label="All Dairy Products"
                                            checked={formData.all_dairy_products === 1}
                                            onChange={() => handleCheckboxChange('all_dairy_products')}
                                            className="mb-3 custom-checkbox"
                                            style={{ marginLeft: '20px' }}
                                        />
                                        <Form.Check
                                            type="checkbox"
                                            id="is_other"
                                            label="Other"
                                            checked={formData.is_other === 1}
                                            onChange={() => handleCheckboxChange('is_other')}
                                            className="mb-3 custom-checkbox"
                                            style={{ marginLeft: '20px' }}
                                        />
                                        {formData.is_other === 1 && (
                                            <Form.Group className="mb-2 ms-4">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Specify other allergies"
                                                    value={formData.other_specify}
                                                    onChange={(e) => handleInputChange('other_specify', e.target.value)}
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                </Row>
                                <hr className="my-4" />
                                <h5 className="mb-3">Insect Stings/Bites</h5>
                                <Row className="mb-3">
                                    <Col md="12">
                                        <Form.Check
                                            type="checkbox"
                                            id="insect_stings_bites"
                                            label="Has Insect Sting/Bite Allergy"
                                            checked={formData.insect_stings_bites === 1}
                                            onChange={() => handleCheckboxChange('insect_stings_bites')}
                                            className="mb-3 custom-checkbox"
                                        />
                                        {formData.insect_stings_bites === 1 && (
                                            <>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Specify Insect Type</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="e.g., Bee sting, Wasp, etc."
                                                        value={formData.insect_stings_bites_specify}
                                                        onChange={(e) => handleInputChange('insect_stings_bites_specify', e.target.value)}
                                                    />
                                                </Form.Group>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Medication for Insect Sting/Bite</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        placeholder="e.g., Apply ice, Benadryl syrup"
                                                        value={formData.insect_stings_bites_medication}
                                                        onChange={(e) => handleInputChange('insect_stings_bites_medication', e.target.value)}
                                                    />
                                                </Form.Group>
                                            </>
                                        )}
                                    </Col>
                                </Row>
                                <hr className="my-4" />
                                <h5 className="mb-3">Symptoms & Emergency Treatment</h5>
                                <Row className="mb-3">
                                    <Col md="12">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Symptoms *</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                placeholder="Describe allergic reaction symptoms"
                                                value={formData.symptoms}
                                                onChange={(e) => handleInputChange('symptoms', e.target.value)}
                                                required
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Emergency Treatment Procedures *</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                placeholder="Describe emergency treatment steps"
                                                value={formData.emergency_treatment_procedures}
                                                onChange={(e) => handleInputChange('emergency_treatment_procedures', e.target.value)}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <hr className="my-4" />
                                <h5 className="mb-3">EpiPen Information</h5>
                                <Row className="mb-3">
                                    <Col md="12">
                                        <Form.Check
                                            type="checkbox"
                                            id="require_epi_pen"
                                            label="Requires EpiPen"
                                            checked={formData.require_epi_pen === 1}
                                            onChange={() => handleCheckboxChange('require_epi_pen')}
                                            className="mb-3 custom-checkbox"
                                        />
                                        {formData.require_epi_pen === 1 && (
                                            <Form.Group className="mb-3">
                                                <Form.Label>Location of EpiPen</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="e.g., In the classroom first-aid kit"
                                                    value={formData.location_of_epi_pen}
                                                    onChange={(e) => handleInputChange('location_of_epi_pen', e.target.value)}
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                </Row>
                                <hr className="my-4" />
                                <h5 className="mb-3">Consent & Acknowledgement</h5>
                                <div className="mb-4 p-3 bg-light border rounded">
                                    <p className="mb-2">By signing this form, I acknowledge and agree to the following:</p>
                                    <ul className="mb-0">
                                        <li className="mb-2">I give Harmony Arts Daycare and Education Centre staff permission to administer my child's epi-pen.</li>
                                        <li className="mb-2">I give permission for staff to call 911 immediately if my child experiences an anaphylactic reaction.</li>
                                        <li className="mb-2">Staff will follow the procedures in this action plan whether or not I or my emergency contact can be reached.</li>
                                        <li className="mb-0">I am responsible for informing the daycare of any required updates to this plan.</li>
                                    </ul>
                                </div>
                                <h5 className="mb-3">Parent Signature</h5>
                                <Row className="mb-3">
                                    <Col md="12">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Parent Signature *</Form.Label>
                                            <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '200px' }}>
                                                <SignatureCanvas
                                                    ref={signaturePadRef}
                                                    canvasProps={{
                                                        className: 'signature-canvas',
                                                        style: { width: '100%', height: '100%' }
                                                    }}
                                                />
                                            </div>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="mt-2"
                                                onClick={handleClearSignature}
                                                type="button"
                                            >
                                                Clear Signature
                                            </Button>
                                        </Form.Group>
                                    </Col>
                                    <Col md="6">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Signature Date *</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={formData.parent_signature_date}
                                                onChange={(e) => handleInputChange('parent_signature_date', e.target.value)}
                                                max={new Date().toISOString().split('T')[0]}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <div className="d-flex justify-content-end gap-2 mt-4">
                                    {onCancel && (
                                        <Button
                                            variant="secondary"
                                            onClick={onCancel}
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                    <Button
                                        variant="primary"
                                        type="submit"
                                    >
                                        Submit
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default AllergyActionPlanFormComp;
